import { useState, useEffect, useRef } from 'react';
import { FiPlus, FiFilter, FiClipboard, FiUpload, FiDownload, FiTrash2, FiCheck, FiCheckCircle, FiCircle, FiSlash, FiLink, FiCopy, FiEye } from 'react-icons/fi';
import api from '../../utils/api';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';
import Badge from '../../components/Badge';
import DepartmentSelect from '../../components/DepartmentSelect';
import { formatDate } from '../../utils/dateHelpers';

const TRAINING_TYPES = [
  'Intern',
  'Student',
  'Clinical Attachment',
  'SCFHS Classification',
  'Residency',
  'Fellowship',
  'Master',
  'PhD Requirements',
];

const TRAINING_TYPE_COLORS = {
  Intern: 'active',
  'Clinical Attachment': 'planned',
  'SCFHS Classification': 'expiring',
  Residency: 'valid',
  Fellowship: 'in-progress',
  Master: 'planned',
  'PhD Requirements': 'expiring',
};

const DEPARTMENTS = [
  'Emergency Medicine',
  'Internal Medicine',
  'Surgery',
  'Pediatrics',
  'Obstetrics & Gynecology',
  'Orthopedics',
  'Cardiology',
  'Radiology',
  'Anesthesia',
  'ICU',
  'Laboratory',
  'Pharmacy',
  'Nursing',
  'Dental',
  'Ophthalmology',
  'ENT',
  'Dermatology',
  'Psychiatry',
  'Rehabilitation',
  'Infection Control',
];

const PROCESS_STEPS = [
  { key: 'training_request_received', label: 'Training Request Received', type: 'file', fileField: 'training_request_file' },
  { key: 'chair_approval', label: 'Chairperson Approval', type: 'checkbox' },
  { key: 'training_application_form', label: 'Training Application Form', type: 'file', fileField: 'application_form_file' },
  { key: 'acceptance_letter_issued', label: 'Acceptance Letter Issued by ATA', type: 'file', fileField: 'acceptance_letter_file' },
  { key: 'sent_to_hr', label: 'Sent to HR', type: 'checkbox' },
  { key: 'sent_to_logistics', label: 'Sent to Logistics', type: 'checkbox' },
  { key: 'sent_to_infection_control', label: 'Sent to Infection Control', type: 'checkbox' },
  { key: 'sent_to_university', label: 'Sent to University', type: 'checkbox' },
  { key: 'sent_to_chairperson_rtp', label: 'Sent to Chairperson/RTP', type: 'checkbox' },
  { key: 'general_orientation_completed', label: 'General Orientation Completed', type: 'checkbox' },
  { key: 'payment_received', label: 'Payment Received', type: 'file', fileField: 'payment_receipt_file', skippable: true, skipField: 'payment_not_required' },
  { key: 'end_of_training_evaluation', label: 'End of Training Evaluation', type: 'file', fileField: 'end_evaluation_file' },
];

function getTraineeStatus(startDate, endDate, trainee) {
  if (!startDate || !endDate) return { label: 'Unknown', badge: 'inactive' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  if (today < start) return { label: 'Upcoming', badge: 'planned' };
  if (today > end) return { label: 'Completed', badge: 'completed' };
  return { label: 'Active', badge: 'valid' };
}

function getCompletionPercent(trainee) {
  let done = 0;
  let total = 0;
  for (const step of PROCESS_STEPS) {
    const skipped = step.skippable && trainee[step.skipField];
    if (skipped) continue;
    total++;
    if (step.type === 'file') {
      if (trainee[step.fileField]) done++;
    } else {
      if (trainee[step.key]) done++;
    }
  }
  return total > 0 ? Math.round((done / total) * 100) : 100;
}

export default function PostGradTraining() {
  const [trainees, setTrainees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [processModal, setProcessModal] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterDept, setFilterDept] = useState('');

  const fetchTrainees = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('training_type', filterType);
      if (filterDept) params.append('department', filterDept);
      const query = params.toString();
      const data = await api.get(`/trainees${query ? `?${query}` : ''}`);
      setTrainees(data.trainees || data || []);
    } catch (err) {
      console.error('Failed to fetch trainees:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTrainees(); }, [filterType, filterDept]);

  const handleSave = async (formData) => {
    try {
      if (editItem?.id || editItem?._id) {
        await api.put(`/trainees/${editItem.id || editItem._id}`, formData);
      } else {
        await api.post('/trainees', formData);
      }
      setEditModal(false);
      setEditItem(null);
      fetchTrainees();
    } catch (err) {
      alert(err.message || 'Failed to save trainee');
    }
  };

  const handleDelete = async (row) => {
    if (!confirm(`Delete trainee "${row.trainee_name}"?`)) return;
    try {
      await api.delete(`/trainees/${row.id || row._id}`);
      fetchTrainees();
    } catch (err) {
      alert(err.message || 'Failed to delete trainee');
    }
  };

  const [copiedId, setCopiedId] = useState(null);

  const copyEvalLink = (trainee) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/evaluation/${trainee.id}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedId(trainee.id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = link;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopiedId(trainee.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleProcessUpdate = (updatedTrainee) => {
    setProcessModal(updatedTrainee);
    setTrainees((prev) =>
      prev.map((t) =>
        (t.id || t._id) === (updatedTrainee.id || updatedTrainee._id) ? updatedTrainee : t
      )
    );
  };

  const columns = [
    {
      key: 'trainee_name',
      label: 'Name',
      render: (v) => <span style={{ fontWeight: 600, color: '#2B362E' }}>{v}</span>,
    },
    {
      key: 'training_type',
      label: 'Type',
      render: (v) => <Badge status={TRAINING_TYPE_COLORS[v] || 'inactive'} text={v} />,
    },
    { key: 'department', label: 'Department' },
    { key: 'university_name', label: 'University' },
    { key: 'supervisor_name', label: 'Supervisor' },
    {
      key: 'start_date',
      label: 'Start',
      render: (v) => formatDate(v),
    },
    {
      key: 'end_date',
      label: 'End',
      render: (v) => formatDate(v),
    },
    {
      key: 'training_period',
      label: 'Period',
      render: (v) => v || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, row) => {
        const s = getTraineeStatus(row.start_date, row.end_date, row);
        return <Badge status={s.badge} text={s.label} />;
      },
    },
    {
      key: 'completion',
      label: 'Progress',
      render: (_, row) => {
        const pct = getCompletionPercent(row);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '100px' }}>
            <div style={{ flex: 1, height: '8px', background: '#f0ece4', borderRadius: '999px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: '#6B8063',
                  borderRadius: '999px',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <span style={{ fontSize: '0.75rem', color: '#6b6b6b', whiteSpace: 'nowrap' }}>{pct}%</span>
          </div>
        );
      },
    },
    {
      key: 'process',
      label: 'Process',
      render: (_, row) => (
        <button
          className="btn btn-sm btn-secondary"
          onClick={(e) => { e.stopPropagation(); setProcessModal(row); }}
          style={{ whiteSpace: 'nowrap' }}
        >
          <FiClipboard size={12} style={{ marginRight: '4px' }} />
          View Process
        </button>
      ),
    },
    {
      key: 'eval_link',
      label: 'Evaluation',
      render: (_, row) => (
        <button
          className="btn btn-sm"
          onClick={(e) => { e.stopPropagation(); copyEvalLink(row); }}
          style={{
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            backgroundColor: copiedId === row.id ? '#6B8063' : '#3498DB',
            color: '#fff',
            border: 'none',
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '0.75rem',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
        >
          {copiedId === row.id ? <><FiCheck size={12} /> Copied!</> : <><FiLink size={12} /> Copy Link</>}
        </button>
      ),
    },
  ];

  return (
    <div>
      {/* Top Action Bar */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header" style={{ flexWrap: 'wrap', gap: '12px' }}>
          <h3 style={{ margin: 0 }}>Training</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={styles.filterGroup}>
              <FiFilter size={14} color="#B2AB9C" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                style={styles.filterSelect}
              >
                <option value="">All Types</option>
                {TRAINING_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div style={styles.filterGroup}>
              <select
                value={filterDept}
                onChange={(e) => setFilterDept(e.target.value)}
                style={styles.filterSelect}
              >
                <option value="">All Departments</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => { setEditItem(null); setEditModal(true); }}
            >
              <FiPlus size={14} /> Add Trainee
            </button>
          </div>
        </div>
      </div>

      {/* Main Table */}
      {loading ? (
        <div className="card text-center p-4">
          <div className="spinner" style={{ margin: '40px auto' }} />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={trainees}
          onEdit={(row) => { setEditItem(row); setEditModal(true); }}
          onDelete={handleDelete}
        />
      )}

      {/* Add / Edit Modal */}
      <Modal
        isOpen={editModal}
        onClose={() => { setEditModal(false); setEditItem(null); }}
        title={editItem ? 'Edit Trainee' : 'Add Trainee'}
      >
        <TraineeForm
          trainee={editItem}
          onSave={handleSave}
          onCancel={() => { setEditModal(false); setEditItem(null); }}
        />
      </Modal>

      {/* Process Tracker Modal */}
      {processModal && (
        <ProcessTrackerModal
          trainee={processModal}
          onClose={() => setProcessModal(null)}
          onUpdate={handleProcessUpdate}
        />
      )}
    </div>
  );
}

/* ──────────────────── Trainee Form ──────────────────── */

function TraineeForm({ trainee, onSave, onCancel }) {
  const [form, setForm] = useState({
    trainee_name: trainee?.trainee_name || '',
    training_type: trainee?.training_type || '',
    start_date: trainee?.start_date ? trainee.start_date.slice(0, 10) : '',
    end_date: trainee?.end_date ? trainee.end_date.slice(0, 10) : '',
    department: trainee?.department || '',
    university_name: trainee?.university_name || '',
    supervisor_name: trainee?.supervisor_name || '',
    request_receiving_date: trainee?.request_receiving_date ? trainee.request_receiving_date.slice(0, 10) : '',
    training_fee_sar: trainee?.training_fee_sar || '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (payload.training_fee_sar !== '' && payload.training_fee_sar !== undefined) {
      payload.training_fee_sar = Number(payload.training_fee_sar);
    }
    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-row">
        <div className="form-group">
          <label>Trainee Name *</label>
          <input
            value={form.trainee_name}
            onChange={(e) => set('trainee_name', e.target.value)}
            required
            placeholder="Full name"
          />
        </div>
        <div className="form-group">
          <label>Training Type *</label>
          <select
            value={form.training_type}
            onChange={(e) => set('training_type', e.target.value)}
            required
          >
            <option value="">Select type...</option>
            {TRAINING_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Start Date *</label>
          <input
            type="date"
            value={form.start_date}
            onChange={(e) => set('start_date', e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>End Date *</label>
          <input
            type="date"
            value={form.end_date}
            onChange={(e) => set('end_date', e.target.value)}
            required
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Department</label>
          <DepartmentSelect value={form.department} onChange={(v) => set('department', v)} />
        </div>
        <div className="form-group">
          <label>University Name</label>
          <input
            value={form.university_name}
            onChange={(e) => set('university_name', e.target.value)}
            placeholder="University or institution"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Supervisor Name</label>
          <input
            value={form.supervisor_name}
            onChange={(e) => set('supervisor_name', e.target.value)}
            placeholder="Assigned supervisor"
          />
        </div>
        <div className="form-group">
          <label>Training Request Receiving Date</label>
          <input
            type="date"
            value={form.request_receiving_date}
            onChange={(e) => set('request_receiving_date', e.target.value)}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Training Fee (SAR)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.training_fee_sar}
            onChange={(e) => set('training_fee_sar', e.target.value)}
            placeholder="0.00"
          />
        </div>
        <div className="form-group" />
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary">Save</button>
      </div>
    </form>
  );
}

/* ──────────────────── Process Tracker Modal ──────────────────── */

function ProcessTrackerModal({ trainee, onClose, onUpdate }) {
  const [busy, setBusy] = useState(null);
  const fileRefs = useRef({});

  const traineeId = trainee.id || trainee._id;

  const completionPct = getCompletionPercent(trainee);

  const isStepSkipped = (step) => step.skippable && !!trainee[step.skipField];

  const isStepDone = (step) => {
    if (isStepSkipped(step)) return true;
    if (step.type === 'file') return !!trainee[step.fileField];
    return !!trainee[step.key];
  };

  const handleSkipToggle = async (step) => {
    if (busy) return;
    setBusy(step.key + '_skip');
    try {
      const newVal = !trainee[step.skipField];
      const data = await api.put(`/trainees/${traineeId}`, { [step.skipField]: newVal });
      onUpdate(data.trainee || data || { ...trainee, [step.skipField]: newVal });
    } catch (err) {
      alert(err.message || 'Failed to update');
    } finally {
      setBusy(null);
    }
  };

  const handleToggle = async (step) => {
    if (busy) return;
    setBusy(step.key);
    try {
      const newVal = !trainee[step.key];
      const data = await api.put(`/trainees/${traineeId}`, { [step.key]: newVal });
      onUpdate(data.trainee || data || { ...trainee, [step.key]: newVal });
    } catch (err) {
      alert(err.message || 'Failed to update step');
    } finally {
      setBusy(null);
    }
  };

  const handleFileUpload = async (step, file) => {
    if (busy || !file) return;
    setBusy(step.key);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await api.post(`/trainees/${traineeId}/upload/${step.fileField}`, formData);
      onUpdate(data.trainee || data || { ...trainee, [step.fileField]: true });
    } catch (err) {
      alert(err.message || 'Failed to upload file');
    } finally {
      setBusy(null);
    }
  };

  const handleFileRemove = async (step) => {
    if (busy) return;
    if (!confirm(`Remove uploaded file for "${step.label}"?`)) return;
    setBusy(step.key);
    try {
      const data = await api.put(`/trainees/${traineeId}`, { [step.fileField]: null });
      onUpdate(data.trainee || data || { ...trainee, [step.fileField]: null });
    } catch (err) {
      alert(err.message || 'Failed to remove file');
    } finally {
      setBusy(null);
    }
  };

  const handleFileView = (step) => {
    const token = localStorage.getItem('ata_token');
    const url = `/api/v1/trainees/${traineeId}/download/${step.fileField}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to open file');
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      })
      .catch((err) => alert(err.message || 'Failed to open file'));
  };

  const triggerFileInput = (stepKey) => {
    if (fileRefs.current[stepKey]) {
      fileRefs.current[stepKey].click();
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Process Tracker - ${trainee.trainee_name}`}
    >
      <div>
        {/* Progress Bar */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#2B362E' }}>Overall Progress</span>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#6B8063' }}>{completionPct}%</span>
          </div>
          <div style={styles.progressBarTrack}>
            <div
              style={{
                ...styles.progressBarFill,
                width: `${completionPct}%`,
              }}
            />
          </div>
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {PROCESS_STEPS.map((step, idx) => {
            const skipped = isStepSkipped(step);
            const done = isStepDone(step);
            const isLoading = busy === step.key || busy === step.key + '_skip';
            const hasFile = step.type === 'file' && !!trainee[step.fileField];

            return (
              <div
                key={step.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  backgroundColor: skipped ? 'rgba(178,171,156,0.08)' : done ? 'rgba(107, 128, 99, 0.06)' : '#fafaf8',
                  transition: 'background-color 0.2s',
                  opacity: skipped ? 0.6 : 1,
                }}
              >
                {/* Step Number Circle */}
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    minWidth: '28px',
                    borderRadius: '50%',
                    backgroundColor: skipped ? '#B2AB9C' : done ? '#6B8063' : '#e0dcd4',
                    color: skipped ? '#ffffff' : done ? '#ffffff' : '#8a8478',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    transition: 'all 0.2s',
                  }}
                >
                  {skipped ? <FiSlash size={14} /> : done ? <FiCheck size={14} /> : idx + 1}
                </div>

                {/* Step Label */}
                <span
                  style={{
                    flex: 1,
                    fontSize: '0.85rem',
                    color: skipped ? '#B2AB9C' : done ? '#2B362E' : '#6b6b6b',
                    fontWeight: done && !skipped ? 500 : 400,
                    textDecoration: skipped ? 'line-through' : 'none',
                  }}
                >
                  {step.label}
                  {skipped && <span style={{ fontSize: '0.7rem', marginLeft: '6px', color: '#B2AB9C', textDecoration: 'none', display: 'inline-block' }}>N/A</span>}
                </span>

                {/* Action */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {/* Skip toggle for skippable steps */}
                  {step.skippable && (
                    <button
                      style={{
                        ...styles.stepActionBtn,
                        backgroundColor: skipped ? '#B2AB9C' : '#f0ece4',
                        color: skipped ? '#ffffff' : '#B2AB9C',
                        fontSize: '0.6rem',
                        fontWeight: 600,
                      }}
                      onClick={() => handleSkipToggle(step)}
                      disabled={isLoading}
                      title={skipped ? 'Mark as required' : 'Mark as not required'}
                    >
                      <FiSlash size={13} />
                    </button>
                  )}

                  {!skipped && step.type === 'file' ? (
                    <>
                      <input
                        type="file"
                        ref={(el) => { fileRefs.current[step.key] = el; }}
                        style={{ display: 'none' }}
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(step, file);
                          e.target.value = '';
                        }}
                      />
                      {hasFile && (
                        <button
                          style={{ ...styles.stepActionBtn, color: '#3498DB' }}
                          onClick={() => handleFileView(step)}
                          title="View file"
                        >
                          <FiEye size={13} />
                        </button>
                      )}
                      <button
                        style={{
                          ...styles.stepActionBtn,
                          opacity: isLoading ? 0.5 : 1,
                          cursor: isLoading ? 'not-allowed' : 'pointer',
                        }}
                        onClick={() => triggerFileInput(step.key)}
                        disabled={isLoading}
                        title={hasFile ? 'Replace file' : 'Upload file'}
                      >
                        {isLoading ? (
                          <span style={{ fontSize: '0.7rem' }}>...</span>
                        ) : (
                          <FiUpload size={13} />
                        )}
                      </button>
                      {hasFile && (
                        <button
                          style={{ ...styles.stepActionBtn, color: '#C0392B' }}
                          onClick={() => handleFileRemove(step)}
                          disabled={isLoading}
                          title="Remove file"
                        >
                          <FiTrash2 size={13} />
                        </button>
                      )}
                    </>
                  ) : !skipped && step.type === 'checkbox' ? (
                    <button
                      style={{
                        ...styles.stepActionBtn,
                        backgroundColor: done ? '#6B8063' : '#f0ece4',
                        color: done ? '#ffffff' : '#B2AB9C',
                        opacity: isLoading ? 0.5 : 1,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => handleToggle(step)}
                      disabled={isLoading}
                      title={done ? 'Mark as incomplete' : 'Mark as complete'}
                    >
                      {isLoading ? (
                        <span style={{ fontSize: '0.7rem' }}>...</span>
                      ) : (
                        <FiCheck size={13} />
                      )}
                    </button>
                  ) : null}

                  {/* Status Indicator */}
                  {skipped ? (
                    <FiSlash size={18} color="#B2AB9C" />
                  ) : done ? (
                    <FiCheckCircle size={18} color="#6B8063" />
                  ) : (
                    <FiCircle size={18} color="#B2AB9C" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

/* ──────────────────── Styles ──────────────────── */

const styles = {
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#F5F0E8',
    borderRadius: '8px',
    padding: '4px 8px',
  },
  filterSelect: {
    border: 'none',
    background: 'transparent',
    fontSize: '0.8rem',
    color: '#2B362E',
    outline: 'none',
    cursor: 'pointer',
    padding: '4px 4px',
  },
  progressBarTrack: {
    width: '100%',
    height: '8px',
    backgroundColor: '#f0ece4',
    borderRadius: '999px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#6B8063',
    borderRadius: '999px',
    transition: 'width 0.4s ease',
  },
  stepActionBtn: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: '1px solid #e0dcd4',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6B8063',
    cursor: 'pointer',
    transition: 'all 0.2s',
    padding: 0,
  },
};
